/**
 * P12.5 — Planning Review Observation Log (read-only)
 *
 * In-memory, append-only, fail-silent telemetry sink for the Project
 * Review Gate. Mirrors the pattern of `contractorTelemetry`.
 *
 * Hard rules:
 *   • Append-only. Past entries never mutated.
 *   • No PII. No customer name. No address. No contractValue. No margin.
 *   • Throttle: dedupe identical (eventType, jobId, actorRole, metaHash, observer)
 *     within 5s.
 *   • Never throws — telemetry MUST NOT block UI.
 *   • Observer-tagged entries are excluded from default summary
 *     (mirrors `getRealContractorTelemetry`).
 */
import { supabase } from "@/integrations/supabase/client";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";

export type PlanningObservationEventType =
  | "review_opened"
  | "approved_as_is"
  | "override_applied"
  | "pending_block_viewed";

export interface PlanningObservationEntry {
  id: string;
  jobId: string;
  eventType: PlanningObservationEventType;
  actorRole: string;
  timestamp: string;
  metadata: Record<string, unknown>;
  /** True when emitted from a SuperAdmin Observer session. */
  observer?: boolean;
}

const THROTTLE_WINDOW_MS = 5000;
const MAX = 500;

const log: PlanningObservationEntry[] = [];
const lastSeen: Map<string, number> = new Map();

function metaHash(meta: Record<string, unknown>): string {
  try {
    return JSON.stringify(meta);
  } catch {
    return "";
  }
}

function genId(): string {
  return `pob_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/* ── Forbidden-key guard ────────────────────────────────────────────── */
const FORBIDDEN_META_KEYS = new Set([
  "contractValue",
  "customerName",
  "customerEmail",
  "customerPhone",
  "address",
  "street",
  "margin",
  "revenue",
]);

function sanitizeMeta(meta: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (FORBIDDEN_META_KEYS.has(k)) continue;
    clean[k] = v;
  }
  return clean;
}

export interface LogPlanningObservationOptions {
  /** When true, marks the entry as a SuperAdmin Observer event. */
  observer?: boolean;
}

export function logPlanningObservation(
  eventType: PlanningObservationEventType,
  jobId: string,
  actorRole: string,
  metadata: Record<string, unknown> = {},
  options: LogPlanningObservationOptions = {},
): PlanningObservationEntry | null {
  try {
    if (!eventType || !jobId || !actorRole) return null;
    const observer = options.observer === true;
    const cleanMeta = sanitizeMeta(metadata);
    const key = `${eventType}::${jobId}::${actorRole}::${observer ? "obs" : "real"}::${metaHash(cleanMeta)}`;
    const now = Date.now();
    const prev = lastSeen.get(key);
    if (prev != null && now - prev < THROTTLE_WINDOW_MS) return null;
    lastSeen.set(key, now);

    const entry: PlanningObservationEntry = {
      id: genId(),
      jobId,
      eventType,
      actorRole,
      timestamp: new Date(now).toISOString(),
      metadata: cleanMeta,
      observer,
    };
    log.push(entry);
    if (log.length > MAX) log.shift();
    void persistObservation(entry);
    return entry;
  } catch {
    return null;
  }
}

async function persistObservation(entry: PlanningObservationEntry): Promise<void> {
  try {
    const organizationId = await getCurrentOrganizationId().catch(() => null);
    if (!organizationId) return;
    const { error } = await supabase.from("planning_observation_log").insert({
      id: entry.id,
      organization_id: organizationId,
      job_id: entry.jobId === "ALL_PENDING" ? null : entry.jobId,
      event_type: entry.eventType,
      actor_role: entry.actorRole,
      observer: entry.observer === true,
      meta: entry.metadata ?? {},
      created_at: entry.timestamp,
    });
    if (error) {
      console.warn("[planningReview.observation] DB persist failed:", error.message);
    }
  } catch (error) {
    console.warn("[planningReview.observation] DB persist threw:", error);
  }
}

export function getPlanningObservationLog(): ReadonlyArray<PlanningObservationEntry> {
  return log;
}

export function clearPlanningObservation(): void {
  log.length = 0;
  lastSeen.clear();
}

/* ── Insights helper ───────────────────────────────────────────────── */

export interface PlanningObservationSummary {
  reviewsOpened: number;
  approvedAsIs: number;
  overridesApplied: number;
  overrideRate: number; // 0..1, of (approved + override)
  pendingBlockHits: number;
}

export interface PlanningObservationSummaryOptions {
  /** Include observer-tagged entries (default: false). */
  includeObserver?: boolean;
}

export function getPlanningObservationSummary(
  options: PlanningObservationSummaryOptions = {},
): PlanningObservationSummary {
  const includeObserver = options.includeObserver === true;
  const source = includeObserver
    ? log
    : log.filter((e) => e.observer !== true);

  let reviewsOpened = 0;
  let approvedAsIs = 0;
  let overridesApplied = 0;
  let pendingBlockHits = 0;

  for (const e of source) {
    switch (e.eventType) {
      case "review_opened":
        reviewsOpened += 1;
        break;
      case "approved_as_is":
        approvedAsIs += 1;
        break;
      case "override_applied":
        overridesApplied += 1;
        break;
      case "pending_block_viewed":
        pendingBlockHits += 1;
        break;
    }
  }

  const decisions = approvedAsIs + overridesApplied;
  const overrideRate = decisions === 0 ? 0 : overridesApplied / decisions;

  return {
    reviewsOpened,
    approvedAsIs,
    overridesApplied,
    overrideRate,
    pendingBlockHits,
  };
}
